import { useNavigate, useParams } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";

import moment, { Moment } from "moment";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { visuallyHidden } from "@mui/utils";
import {
  Badge,
  Box,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Stack,
  TableContainer,
  TablePagination,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { VpnKey } from "@mui/icons-material";

import conf from "../../conf";
import Import from "./Import";
import Request from "./Request";
import Refresh from "../utils/Refresh";
import Spinner from "../utils/Spinner";
import useOpen from "../../hooks/useOpen";
import { useAuth } from "../auth/AuthProvider";
import TagsWithEdit from "../utils/Tags";
import LinearLoader from "../utils/LinearLoader";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import EmptyTableRow from "../utils/Empty";
import ProtectedRoute from "../auth/ProtectedRoute";
import PushWithConfirm from "../utils/PushWithConfirm";
import { SelectProject } from "./Utils";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import PartNumberWithEdit from "../utils/PartNumber";
import { useSmartContent } from "../utils/SmartCell";
import DescriptionWithEdit from "../utils/Description";
import { SelectEmail, SelectStatus } from "../events/Utils";
import { useConfgOrganizationDlmVerifyGlobalStatus } from "../../hooks/confg/base";
import {
  KeySchema,
  useProxyKeyDownload,
  useProxyKeyPush,
  useProxyKeyPushSchedule,
  useProxyKeyPushCancel,
  useProxyKeyRemove,
  useProxyKeys,
  useProxyKeyVerify
} from "../../hooks/proxy/keys";

const KeyRow = ({ data, onClick, isSelected }: { data: KeySchema; onClick: () => void; isSelected: boolean }) => {
  const { project, algorithm, tags, name, status, partNumber, createdAt } = data;

  const { user: self } = useAuth();
  const readOnly = self.activeOrganization.role === "org.auditor";

  return (
    <TableRow
      hover
      selected={isSelected}
      sx={conf.STYLE.LAST_CHILD_NO_BORDER}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <TableCell scope="row">
        <b>{name ?? "--"}</b>
      </TableCell>

      <TableCell align="right">
        {status === "pending" || status === "incomplete" ? <Chip size="small" label={status} color="warning" /> : status}
      </TableCell>

      <TableCell align="right">{algorithm ?? "--"}</TableCell>

      <TableCell align="right">{useSmartContent(partNumber?.toUpperCase() ?? "--", 8)}</TableCell>

      <TableCell align="right">{project.name}</TableCell>

      <TableCell align="right">{moment(createdAt).fromNow()}</TableCell>

      <TableCell align="right">
        <TagsWithEdit id={data.id} type="key" tags={tags} readOnly={readOnly} />
      </TableCell>
    </TableRow>
  );
};

type KeysProps = { collapsible?: boolean };

const Keys = ({ collapsible = false }: KeysProps) => {
  const { projectId = "" } = useParams();

  const { user } = useAuth();
  const navigate = useNavigate();

  const readOnly = user.activeOrganization.role === "org.auditor";

  const { open, toggle } = useOpen(true);
  const [selected, setSelected] = useState<KeySchema | null>(null);
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const paginate = usePagination({ defaultSort: "createdAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const [type, setType] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [dateLt, setDateLt] = useState<Moment | null>(null);
  const [dateGte, setDateGte] = useState<Moment | null>(null);
  const [project, setProject] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);

  const keys = useProxyKeys({
    ...body,
    type,
    email,
    status,
    projectId,
    projectName: project,
    showArchived
  });
  const keyRemove = useProxyKeyRemove();
  const keyVerify = useProxyKeyVerify();
  const keyDownload = useProxyKeyDownload();

  const dlmPush = useProxyKeyPush();
  const dlmCancel = useProxyKeyPushCancel();
  const dlmSchedule = useProxyKeyPushSchedule();
  const dlmVerifyGlobal = useConfgOrganizationDlmVerifyGlobalStatus();

  const toggleFilters = () => setOpenFilters(!openFilters);
  const toggleArchive = (e) => setShowArchived(e.target.checked);

  const handlePush = () => dlmPush.mutate({ keyId: selected?.id || "" });
  const handleCancel = () => dlmCancel.mutate({ keyId: selected?.id || "" });
  const handleSchedule = () => dlmSchedule.mutate({ keyId: selected?.id || "" });
  const handleRemove = () => keyRemove.mutate({ keyId: selected?.id || "" });
  const handleVerify = () => keyVerify.mutate({ keyId: selected?.id || "" });
  const handleDownload = () => keyDownload.mutate({ keyId: selected?.id || "" });

  const handleClose = () => setSelected(null);
  const handleRowClick = (item: any) => setSelected(item);

  const Filters = () => {
    const reset = () => {
      setType("");
      setEmail("");
      setStatus("");
      setProject("");
      setDateLt(null);
      setDateGte(null);
    };

    const Remove = (
      <Tooltip title="remove">
        <RemoveIcon />
      </Tooltip>
    );

    return (
      <Paper elevation={3} sx={{ p: 2 }}>
        <Grid container spacing={2} xs={12} alignItems="center">
          <Grid item sm={2}>
            <SelectStatus
              key={status}
              size="small"
              value={status}
              options={[
                "issued",
                "pending",
                "uploaded",
                "imported",
                "pushed",
                "scheduled",
                "unverified",
                "incomplete",
                "expired",
                "archived",
                "rejected"
              ]}
              onChange={setStatus}
            />
          </Grid>

          <Grid item sm={2}>
            <SelectProject key={project} size="small" value={project} onChange={setProject} returnValue="name" />
          </Grid>

          <Grid item sm={2}>
            <SelectEmail key={email} size="small" value={email} onChange={setEmail} />
          </Grid>

          <Grid item sm={2}>
            <TextField
              fullWidth
              key={"event-date-gte"}
              id="event-date-gte"
              size="small"
              label="From"
              type="datetime-local"
              value={dateGte?.format("YYYY-MM-DDTHH:mm")}
              onChange={(e: any) => setDateGte(moment(e.target.value))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item sm={2}>
            <TextField
              fullWidth
              key={"event-date-lt"}
              id="event-date-lt"
              size="small"
              label="To"
              type="datetime-local"
              value={dateLt?.format("YYYY-MM-DDTHH:mm")}
              onChange={(e: any) => setDateLt(moment(e.target.value))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item sm={2} textAlign="right">
            <FormGroup>
              <FormControlLabel
                label="Show archived"
                control={
                  <Checkbox
                    size="small"
                    name="showArchived"
                    color="primary"
                    checked={showArchived}
                    onChange={toggleArchive}
                  />
                }
                componentsProps={{ typography: { variant: "caption" } }}
              />
            </FormGroup>
          </Grid>

          {/* <Grid item sm={0.5}>
            <Tooltip title="Reset all filters">
              <IconButton onClick={reset}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Grid> */}
        </Grid>

        <Stack direction="row" my={2} spacing={1} flexWrap="wrap">
          {!!email || !!type || !!status || !!project ? (
            <Fragment>
              {!!email && (
                <Chip size="small" color="primary" label={email} onDelete={() => setEmail("")} deleteIcon={Remove} />
              )}

              {!!status && (
                <Chip size="small" color="primary" label={status} onDelete={() => setStatus("")} deleteIcon={Remove} />
              )}

              {!!type && (
                <Chip
                  size="small"
                  color="primary"
                  label={type.replaceAll("_", " ")}
                  onDelete={() => setType("")}
                  deleteIcon={Remove}
                />
              )}

              {!!project && (
                <Chip size="small" label={project} color="primary" onDelete={() => setProject("")} deleteIcon={Remove} />
              )}
            </Fragment>
          ) : (
            <Chip size="small" variant="outlined" label="No filters added" />
          )}
        </Stack>
      </Paper>
    );
  };

  useEffect(() => {
    if (status === "archived") setShowArchived(true);
  }, [status]);

  useEffect(() => {
    if (keyRemove.isSuccess) handleClose();
  }, [keyRemove.isSuccess]);

  useEffect(() => {
    document.title = "[semiauth] | keys";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
        <SectionHeader
          title="Keys"
          icon={<VpnKey />}
          buttons={[
            <Request key="keys-request-modal" />,
            <Import key="keys-import-modal" onSubmit={setSelected} />,
            <Badge
              key={"keys-filter-badge"}
              color="primary"
              badgeContent={[type, email, status, project].filter((key) => !!key).length}
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={toggleFilters}
                endIcon={<FilterAltIcon />}
                startIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: openFilters ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 0.3s"
                    }}
                  />
                }
              >
                Filters
              </Button>
            </Badge>,

            <TablePagination
              key={"keys-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={keys?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh key={"keys-refresh"} onClick={keys.refetch} isFetching={keys.isFetching} />,
            ...(collapsible
              ? [
                  <IconButton
                    key={"keys-collapse"}
                    size="small"
                    onClick={toggle}
                    aria-label="expand"
                    sx={{
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s"
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                ]
              : [])
          ]}
        />

        <Grid container spacing={2}>
          <Grid item xs={8.5}>
            <Paper elevation={3}>
              <TableContainer sx={{ maxHeight: 630 }}>
                <Table aria-label="collapsible table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {conf.COLUMNS.KEYS.map((column) => (
                        <TableCell
                          key={column.label}
                          sortDirection={order}
                          style={{ whiteSpace: "nowrap" }}
                          align={column.isHeader ? "left" : "right"}
                        >
                          <TableSortLabel
                            active={sort === column.key}
                            direction={sort === column.key ? order : "asc"}
                            onClick={(event: React.MouseEvent<unknown>) => handleSort(event, column.key)}
                          >
                            <b>{column.label}</b>
                            {sort === column.key ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc" ? "sorted descending" : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableHead>
                    {openFilters && (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Filters />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableHead>

                  <TableBody>
                    {keys.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <LinearLoader />
                        </TableCell>
                      </TableRow>
                    ) : keys?.data?.data.length === 0 ? (
                      <EmptyTableRow type="keys" colSpan={8} />
                    ) : (
                      keys?.data?.data.map((key) => (
                        <KeyRow
                          key={key.id}
                          data={key}
                          onClick={() => handleRowClick(key)}
                          isSelected={key.id === selected?.id}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/********* DETAIL PANE *********/}
          <Grid item xs={3.5}>
            <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
              {!selected ? (
                <Stack>
                  <Typography variant="h6">No key selected</Typography>

                  <Typography variant="caption">To view details, please select a key from the table.</Typography>
                </Stack>
              ) : (
                <Stack>
                  <Stack direction="row" style={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6">{selected.name}</Typography>

                    <Button color="inherit" size="small" onClick={handleClose}>
                      <CloseIcon />
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography>
                    <strong>File:</strong> {selected.type ?? "--"}
                  </Typography>

                  <Typography>
                    <strong>Email:</strong> {selected.email ?? "--"}
                  </Typography>

                  <Typography>
                    <strong>Created at:</strong> {new Date(selected.createdAt).toLocaleString()}
                  </Typography>

                  {selected.type === "composite" && (
                    <Typography>
                      <strong>Imported components:</strong> {selected.compositeTotalImported}/{selected.compositeTotal}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Grid container>
                    <Grid item sm={6}>
                      <Typography>
                        <strong>Size:</strong> {selected.size ?? "--"}
                      </Typography>
                    </Grid>

                    {["wrapped", "composite"].includes(selected.type) &&
                      !["pushed", "scheduled"].includes(selected.status) && (
                        <Grid item sm={6}>
                          <PartNumberWithEdit
                            id={selected.id}
                            readOnly={readOnly}
                            partNumber={selected.partNumber}
                            alignItems="flex-start"
                            justifyContent="space-between"
                          />
                        </Grid>
                      )}

                    {selected.type === "component" && (
                      <Grid item sm={6}>
                        <Typography>
                          <strong>Target:</strong> {selected.compositeName ?? "--"}
                        </Typography>
                      </Grid>
                    )}

                    {["wrapped", "composite"].includes(selected.type) && (
                      <Fragment>
                        {["imported", "scheduled", "pushed"].includes(selected.status) ? (
                          <Grid item>
                            <Typography>
                              <Tooltip title="COMPUTED: The computed key check value calculated on import. This value is calculated using a zero (0) block in combination with the imported key.">
                                <strong>KCV: </strong>
                              </Tooltip>
                              {selected.computedKcv?.toUpperCase() ?? "--"}
                            </Typography>
                          </Grid>
                        ) : (
                          <Fragment>
                            <Grid item sm={6}>
                              <Typography>
                                <Tooltip
                                  title={`EXPECTED: The expected key check value entered on key upload. This value was computed & entered by ${selected.email}.`}
                                >
                                  <strong>KCV (E): </strong>
                                </Tooltip>
                                {selected.expectedKcv?.toUpperCase() ?? "--"}
                              </Typography>
                            </Grid>

                            <Grid item sm={6}>
                              <Typography>
                                <Tooltip title="COMPUTED: The computed key check value calculated on import. This value is calculated using a zero (0) block in combination with the imported key.">
                                  <strong>KCV (C): </strong>
                                </Tooltip>
                                {selected.computedKcv?.toUpperCase() ?? "--"}
                              </Typography>
                            </Grid>
                          </Fragment>
                        )}
                      </Fragment>
                    )}
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  <DescriptionWithEdit
                    id={selected.id}
                    type="key"
                    readOnly={readOnly}
                    description={selected.description}
                    alignItems="flex-start"
                    justifyContent="space-between"
                  />

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={2}>
                    {selected.status === "uploaded" ? (
                      <Import data={selected} onSubmit={setSelected} />
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={
                          selected.type !== "wrapping" || selected.status === "pending" || selected.status === "rejected"
                        }
                        onClick={handleDownload}
                      >
                        {keyDownload.isPending ? <Spinner sx={{ mx: 4 }} /> : "Download"}
                      </Button>
                    )}

                    <ButtonWithConfirm
                      fullWidth
                      type="Import"
                      label="Verify"
                      color="primary"
                      disabled={!(selected.status === "unverified")}
                      message={
                        <>
                          WARNING: The expected & computed key check values <b>do not match</b>. Please delete or confirm the
                          selected key. Please note that confirming this key import will create a new pending request. This
                          action cannot be undone.
                        </>
                      }
                      status={keyVerify.status}
                      isLoading={keyVerify.isPending}
                      onConfirm={handleVerify}
                      onCancel={handleRemove}
                      onCancelLabel="Delete"
                      onCancelLoading={keyRemove.isPending}
                    />

                    {selected.status === "scheduled" ? (
                      <ButtonWithConfirm
                        fullWidth
                        type="Cancel"
                        label="Cancel"
                        color="primary"
                        status={dlmCancel.status}
                        message={
                          <>
                            WARNING: This action will permanently remove the asset package from the scheduling queue and move
                            the key entry back to an <b>imported</b> state. This action cannot be undone.
                          </>
                        }
                        onConfirm={handleCancel}
                        isLoading={dlmCancel.isPending}
                      />
                    ) : (
                      <PushWithConfirm
                        fullWidth
                        color="primary"
                        message={
                          "WARNING: This action will permanently lock the part number & push the key from the [semiauth] system to a DLM. This action cannot be undone."
                        }
                        disabled={!(selected.status === "imported")}
                        status={dlmVerifyGlobal.isError ? dlmSchedule.status : dlmPush.status}
                        isLoading={dlmVerifyGlobal.isError ? dlmSchedule.isPending : dlmPush.isPending}
                        onConfirm={dlmVerifyGlobal.isError ? handleSchedule : handlePush}
                      />
                    )}

                    <ButtonWithConfirm
                      fullWidth
                      type="Delete"
                      label="Delete"
                      color="primary"
                      message={
                        "WARNING: This action will permanently remove the key from the [semiauth] system. This action cannot be undone."
                      }
                      status={keyRemove.status}
                      disabled={
                        selected.status === "pushed" ||
                        selected.status === "pending" ||
                        selected.status === "archived" ||
                        selected.status === "scheduled" ||
                        selected.status === "incomplete"
                      }
                      isLoading={keyRemove.isPending}
                      onConfirm={handleRemove}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      disabled={!selected.requestId}
                      onClick={() => navigate(`/requests/${selected.requestId}`)}
                    >
                      View request
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </ProtectedRoute>
  );
};

export default Keys;
